import { AxiosError } from "axios";
import uniqueId from "lodash/uniqueId";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { uploadAttachment } from "PFCore/services/attachments/upload_attachment";
import { Attachment } from "PFCore/types";
import { EventKey } from "PFCore/types/event_key";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./add_attachment_button.module.scss";

type AddAttachmentButtonProps = {
  onUploadStart?: (pendingAttachment: Attachment) => void;
  onUploadSuccess: (attachment: Attachment, pendingAttachment: Attachment) => void;
  onUploadError?: (pendingAttachment: Attachment, errorMessage: string) => void;
  disabled?: boolean;
};

export const AddAttachmentButton = ({
  onUploadStart,
  onUploadSuccess,
  onUploadError,
  disabled = false
}: AddAttachmentButtonProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.attachments" });
  const [loading, setLoading] = useState(false);
  const { showGrowl } = useGrowlStoreActions();
  const { getFirstError } = useResponseErrors();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const pendingAttachment = {
      id: uniqueId("fake"),
      pending: true,
      fileName: file.name
    } as unknown as Attachment;

    onUploadStart?.(pendingAttachment);
    setLoading(true);

    uploadAttachment(file).then(
      (attachment: Attachment) => {
        onUploadSuccess(attachment, pendingAttachment);
        setLoading(false);
      },
      (error: AxiosError) => {
        const errorMessage = getFirstError(error) || t("failedUploadAttachment");
        onUploadError?.(pendingAttachment, errorMessage);
        if (errorMessage) {
          showGrowl({ message: errorMessage, kind: "error" });
        }
        setLoading(false);
      }
    );
  };

  const handleClearInputValue = (event: React.MouseEvent<HTMLInputElement>) => {
    (event.target as HTMLInputElement).value = "";
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === EventKey.Enter) {
      inputRef.current?.click();
    }
  };

  return (
    <label className={css.addAttachmentButton}>
      <Button
        tag="a"
        kind="text"
        text={t("addAttachment")}
        disabled={disabled || loading}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      />
      <input
        ref={inputRef}
        disabled={disabled || loading}
        type="file"
        name="attachment"
        onChange={handleChange}
        onClick={handleClearInputValue}
      />
    </label>
  );
};
